import {gql} from '@apollo/client';
import {parseNumber} from '@benchling/js-utils';
import stringify from 'json-stable-stringify';

import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {LegacyMeasurement} from 'constants/MeasurementConstants';
import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {fetchUnitBySymbol} from 'inventory/InventoryUnitUtil';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {
  ExistingBioentityColumnDefinition,
  MeasurementAmountColumnDefinition,
  QuantityColumnDefinition,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {ValueError, Warning} from 'notebook/tables/excess/Util';
import {
  CustomResolutionQueryType,
  InventoryTableQuantityAmountFromSourceContainerQuery,
  InventoryTableQuantityAmountFromSourcePlateQuery,
  InventoryTableQuantityUnitsFromSourceContainerQuery,
  InventoryTableQuantityUnitsFromSourcePlateQuery,
  InventoryTableValidateAddDeductFromSourceContainerQuery,
  InventoryTableValidateAddDeductFromSourcePlateQuery,
  ServerResolutionResult,
  TransferTableValidateQuantityWithSourceContainerQuery,
  TransferTableValidateQuantityWithSourcePlateQuery,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {CustomEval, CustomEvalResult} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {
  formatTypedValue,
  isTypedValueEmpty,
  toStringTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
  ResolveTypedValueToTypeFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {NumberTypedValue, StringTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {InvalidPositionError, parseGridPosition} from 'samples/SampleUtil';
import {PlateSchemaType} from 'samples/storables/StorableSchemaConstants';
import * as TransferUtil from 'samples/transfers/TransferUtil';
import {MinimalSourceContainerSummary} from 'samples/transfers/TransferUtil';
import lruMemoize from 'util/lruMemoize';
import {capitalize} from 'util/NameUtil';
import {Optional} from 'util/Types';

import {GetContainerQuantity, GetContainerQuantityVariables} from './__generated/GetContainerQuantity';
import {
  GetPlateWithWellQuantities,
  GetPlateWithWellQuantitiesVariables,
} from './__generated/GetPlateWithWellQuantities';
import {extractContainerId} from './extractContainerId';
import extractPlateId from './extractPlateId';

type WellOrInaccessible = NonNullable<
  ExtractByTypename<GetPlateWithWellQuantities['plates'][number], 'PlateNode'>['cells'][number][number]
>['container'];

export const GET_CONTAINER_QUANTITY_QUERY = gql`
  query GetContainerQuantity($containerIds: [StrictString]!) @skipAudit @engComponent(name: INVENTORY) {
    containers(ids: $containerIds) {
      ... on ContainerNode {
        id
        name
        quantityJson
        storableSchema {
          ... on StorableSchemaInterface {
            id
          }
        }
        ...HasArchiveRecordGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const GET_PLATE_WITH_WELL_QUANTITIES_QUERY = gql`
  query GetPlateWithWellQuantities($plateIds: [StrictString!]!) @skipAudit @engComponent(name: INVENTORY) {
    plates(ids: $plateIds) {
      ... on PlateNode {
        id
        name
        storableSchema {
          ... on PlateSchemaNode {
            id
            height
            width
            plateSchemaType
          }
        }
        cells {
          container {
            ... on ContainerNode {
              id
              name
              quantityJson
            }
            ...InaccessibleInfoGraphQL
          }
        }
        ...HasArchiveRecordGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

// To mock our GraphQL call, we need to wrap it in a method.
// This is different from most of our GraphQL tests, where we mock the Apollo cache.
// Here, mocking the cache won't work, because our fetch policy is `'no-cache'`.
export const stubbableFunctions = {
  async doContainerQuery(containerId: string): Promise<GetContainerQuantity> {
    return await doQueryWithoutCache<GetContainerQuantity, GetContainerQuantityVariables>(
      {query: GET_CONTAINER_QUANTITY_QUERY},
      {variables: {containerIds: [containerId]}}
    );
  },

  async doPlateQuery(plateId: string): Promise<GetPlateWithWellQuantities> {
    return await doQueryWithoutCache<GetPlateWithWellQuantities, GetPlateWithWellQuantitiesVariables>(
      {query: GET_PLATE_WITH_WELL_QUANTITIES_QUERY},
      {variables: {plateIds: [plateId]}}
    );
  },
};

export function getNumericCustomEvalResult({
  amountTypedValue,
  allowNegativeAmount,
}: {
  amountTypedValue: TypedValue;
  allowNegativeAmount: boolean;
}): {val: NumberTypedValue | StringTypedValue; error: ValueError | Warning | null; locked: false} {
  const formattedValue = formatTypedValue(amountTypedValue);
  const stringResolvedValue: StringTypedValue = {
    type: BaseTypedValueType.STRING,
    rawValue: formattedValue,
  };
  if (formattedValue === '') {
    return {
      val: stringResolvedValue,
      error: null,
      locked: false,
    };
  }
  const rawValue = parseNumber(formattedValue);
  const numberError = allowNegativeAmount
    ? null
    : new ValueError(`${formattedValue} is not a valid positive number.`);
  if (rawValue == null) {
    return {
      val: stringResolvedValue,
      error: numberError,
      locked: false,
    };
  }

  const resolvedValue: NumberTypedValue = {
    type: BaseTypedValueType.NUMBER,
    rawValue,
    defaultPrecision: null,
  };
  return {
    val: resolvedValue,
    error: rawValue < 0 ? numberError : null,
    locked: false,
  };
}

// TODO(BNCH-14955): dedupe with getCellWithResolvedNumberValue from the old transfer table?
export function getNonnegativeCustomEvalResult(quantityTypedValue: TypedValue): {
  val: NumberTypedValue | StringTypedValue;
  error: ValueError | Warning | null;
  locked: false;
} {
  return getNumericCustomEvalResult({amountTypedValue: quantityTypedValue, allowNegativeAmount: false});
}

export function validateQuantityAgainstInput({
  inputContainer,
  transferQuantityValue,
  transferQuantityUnits,
}: {
  inputContainer: MinimalSourceContainerSummary;
  transferQuantityValue: number;
  transferQuantityUnits: string;
}): ServerResolutionResult {
  const {isValid: transferQuantityIsValid, quantityErrorsAreBlocking} =
    TransferUtil.validateAllTransferQuantities([
      {
        source: inputContainer,
        transferQuantity: {value: transferQuantityValue, units: transferQuantityUnits},
      },
    ]);
  if (!transferQuantityIsValid) {
    return {
      type: undefined,
      error: TransferUtil.getTransferQuantityValidationText(inputContainer, quantityErrorsAreBlocking),
      resolvedValue: undefined,
      isWarning: !quantityErrorsAreBlocking,
    };
  }

  return makeCustomResolutionResultFromTypedValue(null);
}

async function prefillUnitsCache(...symbols: Array<string | null>): Promise<void> {
  // We don't need to return the units here, merely ensure they are populated in the Apollo cache when
  // functions further down the call stack query them.
  await Promise.all(symbols.filter((symbol) => symbol != null).map((symbol) => fetchUnitBySymbol(symbol)));
}

export const TransferQuantityResolver = {
  validateQuantityWithSourceContainer: async (
    query: TransferTableValidateQuantityWithSourceContainerQuery
  ): Promise<ServerResolutionResult> => {
    const data = await stubbableFunctions.doContainerQuery(query.containerId);

    const container = data.containers[0];
    if (isInaccessible(container)) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    const quantity: Optional<LegacyMeasurement> = container.quantityJson;
    if (quantity == null) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    await prefillUnitsCache(query.quantityUnits, quantity.units);

    return validateQuantityAgainstInput({
      inputContainer: {...container, quantity},
      transferQuantityValue: query.quantityValue,
      transferQuantityUnits: query.quantityUnits,
    });
  },

  validateQuantityWithSourcePlate: async (
    query: TransferTableValidateQuantityWithSourcePlateQuery
  ): Promise<ServerResolutionResult> => {
    const data = await stubbableFunctions.doPlateQuery(query.plateId);

    const plate = data.plates[0];
    if (isInaccessible(plate)) {
      return makeCustomResolutionResultFromTypedValue(null);
    }
    assert(plate.storableSchema.__typename === 'PlateSchemaNode', 'Expected a plate schema');

    let rowIndex: number;
    let columnIndex: number;
    try {
      ({rowIndex, columnIndex} = parseGridPosition({
        position: query.position,
        gridHeight: plate.storableSchema.height,
        gridWidth: plate.storableSchema.width,
      }));
    } catch (e) {
      if (e instanceof InvalidPositionError) {
        return makeCustomResolutionResultFromTypedValue(null);
      } else {
        throw e;
      }
    }

    const inputWell = plate.cells[rowIndex][columnIndex]?.container;
    if (isInaccessible(inputWell)) {
      return makeCustomResolutionResultFromTypedValue(null);
    }
    const quantity: Optional<LegacyMeasurement> = inputWell?.quantityJson;
    if (inputWell == null || quantity == null) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    await prefillUnitsCache(query.quantityUnits, quantity.units);

    return validateQuantityAgainstInput({
      inputContainer: {...inputWell, quantity},
      transferQuantityValue: query.quantityValue,
      transferQuantityUnits: query.quantityUnits,
    });
  },
};

export const TransferQuantityCustomEval = {
  quantityWithSourceEntityCustomEval: (
    quantityTypedValue: TypedValue,
    columnDefinition: QuantityColumnDefinition
  ): CustomEvalResult => getNonnegativeCustomEvalResult(quantityTypedValue),

  quantityWithSourceContainerCustomEval: (
    quantityTypedValue: TypedValue,
    columnDefinition: QuantityColumnDefinition,
    [containerTypedValue]: [TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ): CustomEvalResult => {
    const numericResult = getNonnegativeCustomEvalResult(quantityTypedValue);
    if (numericResult.val.type !== BaseTypedValueType.NUMBER || numericResult.error) {
      return numericResult;
    }

    const containerId = extractContainerId(containerTypedValue);
    if (containerId == null) {
      return {val: quantityTypedValue, error: null, locked: false};
    }

    const {resolvedValue, error} = resolveFromResolutionQuery(
      quantityTypedValue,
      {
        customResolutionQueryType:
          CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_QUANTITY_WITH_SOURCE_CONTAINER,
        containerId,
        quantityValue: numericResult.val.rawValue,
        quantityUnits: columnDefinition.units,
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    const val = resolvedValue.type === BaseTypedValueType.NUMBER ? resolvedValue : numericResult.val;
    return {val, error, locked: false};
  },

  quantityWithSourcePlateCustomEval: (
    quantityTypedValue: TypedValue,
    columnDefinition: QuantityColumnDefinition,
    [plateTypedValue, positionTypedValue]: [TypedValue | null, TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ): CustomEvalResult => {
    const numericResult = getNonnegativeCustomEvalResult(quantityTypedValue);
    if (numericResult.val.type !== BaseTypedValueType.NUMBER || numericResult.error) {
      return numericResult;
    }

    if (!positionTypedValue || isTypedValueEmpty(positionTypedValue)) {
      return {val: quantityTypedValue, error: null, locked: false};
    }

    const position = formatTypedValue(positionTypedValue);
    const plateId = extractPlateId(plateTypedValue);
    if (plateId == null) {
      return {val: quantityTypedValue, error: null, locked: false};
    }

    const {resolvedValue, error} = resolveFromResolutionQuery(
      quantityTypedValue,
      {
        customResolutionQueryType:
          CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_QUANTITY_WITH_SOURCE_PLATE,
        plateId,
        position,
        quantityValue: numericResult.val.rawValue,
        quantityUnits: columnDefinition.units,
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    const val = resolvedValue.type === BaseTypedValueType.NUMBER ? resolvedValue : numericResult.val;
    return {val, error, locked: false};
  },
};

export function validateAddDeductAgainstQuantity({
  inputQuantity,
  addDeductValue,
  isPlateWell,
}: {
  inputQuantity: LegacyMeasurement;
  addDeductValue: number;
  isPlateWell: boolean;
}): ServerResolutionResult {
  const storableLabel = isPlateWell ? 'well' : 'container';
  if (inputQuantity.units == null || inputQuantity.value == null) {
    return {
      type: undefined,
      error: `Cannot add or deduct from a ${storableLabel} with an unspecified quantity.`,
      resolvedValue: undefined,
    };
  }
  if (inputQuantity.value === 0 && addDeductValue < 0) {
    return {
      type: undefined,
      error: `Cannot deduct from an empty ${storableLabel}.`,
      resolvedValue: undefined,
    };
  }
  if (inputQuantity.value + addDeductValue < 0) {
    return {
      type: undefined,
      error: `A larger quantity is being deducted than exists in the ${storableLabel}. ${capitalize(
        storableLabel
      )} quantity will be set to 0.`,
      isWarning: true,
      resolvedValue: undefined,
    };
  }
  return makeCustomResolutionResultFromTypedValue(null);
}

function getQuantityFromWell(well?: WellOrInaccessible): Optional<LegacyMeasurement> {
  if (isInaccessible(well)) {
    return null;
  }
  return well?.quantityJson;
}

export const InventoryQuantityResolver = {
  autopopulateContainerQuantityAmount: async (
    query: InventoryTableQuantityAmountFromSourceContainerQuery
  ): Promise<ServerResolutionResult> => {
    const data = await stubbableFunctions.doContainerQuery(query.containerId);
    const container = data.containers[0];
    if (isInaccessible(container)) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    if (query.containerSchemaId != null && query.containerSchemaId !== container.storableSchema.id) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    const quantity: Optional<LegacyMeasurement> = container.quantityJson;
    if (quantity == null || quantity.value == null) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    const typedValue = toStringTypedValue(quantity.value.toString());
    return makeCustomResolutionResultFromTypedValue(typedValue);
  },

  autopopulateContainerQuantityUnits: async (
    query: InventoryTableQuantityUnitsFromSourceContainerQuery
  ): Promise<ServerResolutionResult> => {
    const data = await stubbableFunctions.doContainerQuery(query.containerId);
    const container = data.containers[0];
    if (isInaccessible(container)) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    if (query.containerSchemaId != null && query.containerSchemaId !== container.storableSchema.id) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    const quantity: Optional<LegacyMeasurement> = container.quantityJson;
    if (quantity == null || quantity.units == null) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    return makeCustomResolutionResultFromTypedValue(toStringTypedValue(quantity.units));
  },

  autopopulatePlateQuantityAmount: async (
    query: InventoryTableQuantityAmountFromSourcePlateQuery
  ): Promise<ServerResolutionResult> => {
    const data = await stubbableFunctions.doPlateQuery(query.plateId);

    const plate = data.plates[0];
    if (isInaccessible(plate)) {
      return makeCustomResolutionResultFromTypedValue(null);
    }
    assert(plate.storableSchema.__typename === 'PlateSchemaNode', 'Expected a plate schema');

    if (query.plateSchemaId != null && query.plateSchemaId !== plate.storableSchema.id) {
      return makeCustomResolutionResultFromTypedValue(null);
    }
    if (plate.storableSchema.plateSchemaType !== PlateSchemaType.FIXED_PLATE) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    let rowIndex: number;
    let columnIndex: number;
    try {
      ({rowIndex, columnIndex} = parseGridPosition({
        position: query.position,
        gridHeight: plate.storableSchema.height,
        gridWidth: plate.storableSchema.width,
      }));
    } catch (e) {
      if (e instanceof InvalidPositionError) {
        return makeCustomResolutionResultFromTypedValue(null);
      } else {
        throw e;
      }
    }

    const quantity = getQuantityFromWell(plate.cells[rowIndex][columnIndex]?.container);
    if (quantity == null || quantity.value == null) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    const typedValue = toStringTypedValue(quantity.value.toString());
    return makeCustomResolutionResultFromTypedValue(typedValue);
  },

  autopopulatePlateQuantityUnits: async (
    query: InventoryTableQuantityUnitsFromSourcePlateQuery
  ): Promise<ServerResolutionResult> => {
    const data = await stubbableFunctions.doPlateQuery(query.plateId);

    const plate = data.plates[0];
    if (isInaccessible(plate)) {
      return makeCustomResolutionResultFromTypedValue(null);
    }
    assert(plate.storableSchema.__typename === 'PlateSchemaNode', 'Expected a plate schema');

    if (query.plateSchemaId != null && query.plateSchemaId !== plate.storableSchema.id) {
      return makeCustomResolutionResultFromTypedValue(null);
    }
    if (plate.storableSchema.plateSchemaType !== PlateSchemaType.FIXED_PLATE) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    let rowIndex: number;
    let columnIndex: number;
    try {
      ({rowIndex, columnIndex} = parseGridPosition({
        position: query.position,
        gridHeight: plate.storableSchema.height,
        gridWidth: plate.storableSchema.width,
      }));
    } catch (e) {
      if (e instanceof InvalidPositionError) {
        return makeCustomResolutionResultFromTypedValue(null);
      } else {
        throw e;
      }
    }

    const quantity: Optional<LegacyMeasurement> = getQuantityFromWell(
      plate.cells[rowIndex][columnIndex]?.container
    );
    if (quantity == null || quantity.units == null) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    return makeCustomResolutionResultFromTypedValue(toStringTypedValue(quantity.units));
  },

  validateAddDeductFromSourceContainer: async (
    query: InventoryTableValidateAddDeductFromSourceContainerQuery
  ): Promise<ServerResolutionResult> => {
    const data = await stubbableFunctions.doContainerQuery(query.containerId);
    const container = data.containers[0];
    if (isInaccessible(container)) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    const quantity: Optional<LegacyMeasurement> = container.quantityJson;
    if (quantity == null) {
      return makeCustomResolutionResultFromTypedValue(null);
    }
    if (isArchived(container)) {
      return {
        type: undefined,
        error: `Cannot add or deduct from an archived container.`,
        resolvedValue: undefined,
      };
    }
    return validateAddDeductAgainstQuantity({
      inputQuantity: quantity,
      addDeductValue: query.addDeductValue,
      isPlateWell: false,
    });
  },

  validateAddDeductFromSourcePlate: async (
    query: InventoryTableValidateAddDeductFromSourcePlateQuery
  ): Promise<ServerResolutionResult> => {
    const data = await stubbableFunctions.doPlateQuery(query.plateId);

    const plate = data.plates[0];
    if (isInaccessible(plate)) {
      return makeCustomResolutionResultFromTypedValue(null);
    }
    assert(plate.storableSchema.__typename === 'PlateSchemaNode', 'Expected a plate schema');

    let rowIndex: number;
    let columnIndex: number;
    try {
      ({rowIndex, columnIndex} = parseGridPosition({
        position: query.position,
        gridHeight: plate.storableSchema.height,
        gridWidth: plate.storableSchema.width,
      }));
    } catch (e) {
      if (e instanceof InvalidPositionError) {
        return makeCustomResolutionResultFromTypedValue(null);
      } else {
        throw e;
      }
    }

    const quantity: Optional<LegacyMeasurement> = getQuantityFromWell(
      plate.cells[rowIndex][columnIndex]?.container
    );

    if (quantity == null) {
      return makeCustomResolutionResultFromTypedValue(null);
    }
    if (isArchived(plate)) {
      return {
        type: undefined,
        error: `Cannot add or deduct from wells in an archived plate.`,
        resolvedValue: undefined,
      };
    }

    return validateAddDeductAgainstQuantity({
      inputQuantity: quantity,
      addDeductValue: query.addDeductValue,
      isPlateWell: true,
    });
  },
};

function _getQuantityAmountFromSourceContainerCustomEval<CustomEvalOptions>({
  containerSchemaId,
}: {containerSchemaId?: string | null} = {}): CustomEval<CustomEvalOptions> {
  return (
    quantityTypedValue: TypedValue,
    columnDefinition: ExistingBioentityColumnDefinition,
    [containerTypedValue]: [TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    if (!isTypedValueEmpty(quantityTypedValue)) {
      return {val: quantityTypedValue, error: null, locked: false};
    }

    const containerId = extractContainerId(containerTypedValue);
    if (containerId == null) {
      return {val: quantityTypedValue, error: null, locked: false};
    }

    const {resolvedValue, error} = resolveFromResolutionQuery(
      quantityTypedValue,
      {
        customResolutionQueryType:
          CustomResolutionQueryType.INVENTORY_TABLE_QUANTITY_AMOUNT_FROM_SOURCE_CONTAINER,
        containerId,
        ...(containerSchemaId != null && {containerSchemaId}),
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {val: resolvedValue, error, locked: false};
  };
}

function _getQuantityUnitsFromSourceContainerCustomEval<CustomEvalOptions>({
  containerSchemaId,
}: {containerSchemaId?: string | null} = {}): CustomEval<CustomEvalOptions> {
  return (
    quantityTypedValue: TypedValue,
    columnDefinition: ExistingBioentityColumnDefinition,
    [containerTypedValue]: [TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    if (!isTypedValueEmpty(quantityTypedValue)) {
      return {val: quantityTypedValue, error: null, locked: false};
    }

    const containerId = extractContainerId(containerTypedValue);
    if (containerId == null) {
      return {val: quantityTypedValue, error: null, locked: false};
    }

    const {resolvedValue, error} = resolveFromResolutionQuery(
      quantityTypedValue,
      {
        customResolutionQueryType:
          CustomResolutionQueryType.INVENTORY_TABLE_QUANTITY_UNITS_FROM_SOURCE_CONTAINER,
        containerId,
        ...(containerSchemaId != null && {containerSchemaId}),
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {val: resolvedValue, error, locked: false};
  };
}

function _getQuantityAmountFromSourcePlateCustomEval<CustomEvalOptions>({
  plateSchemaId,
}: {plateSchemaId?: string | null} = {}): CustomEval<CustomEvalOptions> {
  return (
    quantityTypedValue: TypedValue,
    columnDefinition: QuantityColumnDefinition,
    [plateTypedValue, positionTypedValue]: [TypedValue | null, TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    if (!positionTypedValue || isTypedValueEmpty(positionTypedValue)) {
      return {val: quantityTypedValue, error: null, locked: false};
    }

    const position = formatTypedValue(positionTypedValue);
    const plateId = extractPlateId(plateTypedValue);
    if (plateId == null) {
      return {val: quantityTypedValue, error: null, locked: false};
    }

    const {resolvedValue, error} = resolveFromResolutionQuery(
      quantityTypedValue,
      {
        customResolutionQueryType:
          CustomResolutionQueryType.INVENTORY_TABLE_QUANTITY_AMOUNT_FROM_SOURCE_PLATE,
        plateId,
        position,
        ...(plateSchemaId != null && {plateSchemaId}),
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {val: resolvedValue, error, locked: false};
  };
}

function _getQuantityUnitsFromSourcePlateCustomEval<CustomEvalOptions>({
  plateSchemaId,
}: {plateSchemaId?: string | null} = {}): CustomEval<CustomEvalOptions> {
  return (
    quantityTypedValue: TypedValue,
    columnDefinition: QuantityColumnDefinition,
    [plateTypedValue, positionTypedValue]: [TypedValue | null, TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    if (!positionTypedValue || isTypedValueEmpty(positionTypedValue)) {
      return {val: quantityTypedValue, error: null, locked: false};
    }

    const position = formatTypedValue(positionTypedValue);
    const plateId = extractPlateId(plateTypedValue);
    if (plateId == null) {
      return {val: quantityTypedValue, error: null, locked: false};
    }

    const {resolvedValue, error} = resolveFromResolutionQuery(
      quantityTypedValue,
      {
        customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_QUANTITY_UNITS_FROM_SOURCE_PLATE,
        plateId,
        position,
        ...(plateSchemaId != null && {plateSchemaId}),
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {val: resolvedValue, error, locked: false};
  };
}

export const InventoryQuantityCustomEval = {
  getQuantityAmountFromSourceContainerCustomEval: lruMemoize(
    _getQuantityAmountFromSourceContainerCustomEval,
    {max: 100},
    (...args) => stringify(args)
  ),

  getQuantityAmountFromSourcePlateCustomEval: lruMemoize(
    _getQuantityAmountFromSourcePlateCustomEval,
    {max: 100},
    (...args) => stringify(args)
  ),

  getQuantityUnitsFromSourceContainerCustomEval: lruMemoize(
    _getQuantityUnitsFromSourceContainerCustomEval,
    {max: 100},
    (...args) => stringify(args)
  ),

  getQuantityUnitsFromSourcePlateCustomEval: lruMemoize(
    _getQuantityUnitsFromSourcePlateCustomEval,
    {max: 100},
    (...args) => stringify(args)
  ),

  addDeductFromSourceContainerCustomEval: (
    typedValue: TypedValue,
    columnDefinition: MeasurementAmountColumnDefinition,
    [containerTypedValue]: [TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn,
    resolveTypedValueToType: ResolveTypedValueToTypeFn
  ): CustomEvalResult => {
    const numericResult = getNumericCustomEvalResult({
      amountTypedValue: typedValue,
      allowNegativeAmount: true,
    });
    if (numericResult.val.type !== BaseTypedValueType.NUMBER || numericResult.error) {
      return numericResult;
    }
    const containerId = extractContainerId(containerTypedValue);
    if (containerId == null) {
      return {val: typedValue, error: null, locked: false};
    }

    const {resolvedValue: customResolvedValue, error} = resolveFromResolutionQuery(
      typedValue,
      {
        customResolutionQueryType:
          CustomResolutionQueryType.INVENTORY_TABLE_VALIDATE_ADD_DEDUCT_FROM_SOURCE_CONTAINER,
        type: ResolutionQueryType.CUSTOM,
        containerId,
        addDeductValue: numericResult.val.rawValue,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {val: customResolvedValue, error, locked: false};
  },

  addDeductFromSourcePlateCustomEval: (
    typedValue: TypedValue,
    columnDefinition: MeasurementAmountColumnDefinition,
    [plateTypedValue, positionTypedValue]: [TypedValue | null, TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn,
    resolveTypedValueToType: ResolveTypedValueToTypeFn
  ): CustomEvalResult => {
    const numericResult = getNumericCustomEvalResult({
      amountTypedValue: typedValue,
      allowNegativeAmount: true,
    });
    if (numericResult.val.type !== BaseTypedValueType.NUMBER || numericResult.error) {
      return numericResult;
    }
    if (!positionTypedValue || isTypedValueEmpty(positionTypedValue)) {
      return {val: typedValue, error: null, locked: false};
    }

    const position = formatTypedValue(positionTypedValue);
    const plateId = extractPlateId(plateTypedValue);
    if (plateId == null) {
      return {val: typedValue, error: null, locked: false};
    }

    const {resolvedValue: customResolvedValue, error} = resolveFromResolutionQuery(
      typedValue,
      {
        customResolutionQueryType:
          CustomResolutionQueryType.INVENTORY_TABLE_VALIDATE_ADD_DEDUCT_FROM_SOURCE_PLATE,
        type: ResolutionQueryType.CUSTOM,
        plateId,
        position,
        addDeductValue: numericResult.val.rawValue,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {val: customResolvedValue, error, locked: false};
  },
};
